import { FormikHelpers } from 'formik';
import { AxiosRequestConfig } from 'axios';
import { useEffect, useRef, useState } from 'react';
import { callApi } from 'src/core/api/callApi';
import { clearCacheForUrl } from 'src/core/api/cache/idbUtility';
import { useMessageContext } from 'src/shared/support/MessageContext';
import { getAllPurchaseOrders, getPurchaseOrderById } from 'src/core/api/apiRequests';
import { createOrUpdatePurchaseOrder, createPurchaseOrderRowsById, deletePurchaseOrderRowById, updatePurchaseOrderRowsById, cancelPurchaseOrder } from 'src/core/api/apiRequests';
import { buildSubmitRequest } from 'src/core/forms/submit/buildSubmitRequest';
import { createPurchaseOrderPolicy, STATUS_ID } from './PurchaseOrderRules';

export const usePurchaseOrderSubmit = () => {
  const [result, setResult] = useState<any>(null);
  const [submitting, setSubmitting] = useState(false);
  const active = useRef(false);
  const mounted = useRef(true);
  const cancel = useRef<() => void>();
  const created = useRef<any>(null);
  const completed = useRef(new Set<string>());
  const identity = useRef<any>();
  const { updateMessageStatus } = useMessageContext();
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; cancel.current?.(); }; }, []);
  const submit = async ( values: any, changedRows: any[], helpers: FormikHelpers<any>, options?: { nextStatusId?: number; guards?: { formDirty: boolean; rowsChanged: boolean }; baselineRows?: any[] } ) => {
    if (active.current) return;
    active.current = true;
    setSubmitting(true);
    if (identity.current !== values.PurchaseOrderId) {
      identity.current = values.PurchaseOrderId;
      created.current = null;
      completed.current.clear();
    }
    const send = (request: AxiosRequestConfig) => new Promise<any>((resolve, reject) => {
      cancel.current = callApi({ request, config: { isMountedRef: () => mounted.current, updateMessageStatus, logLevel: ['error'], resolve, reject } });
    });
    try {
      if (!values.PurchaseOrderId && !created.current) created.current = (await send(buildPurchaseOrderRequest({ values, orderRows: changedRows }))) ?? {};
      if (!mounted.current) return;
      const id = values.PurchaseOrderId || created.current?.PurchaseOrderId;
      if (!id) {
        updateMessageStatus({ requestUrl: getAllPurchaseOrders(500, 0).url, status: 'error', message: 'The create response did not identify the Purchase Order. Check the created record before retrying.' });
        return;
      }
      if (values.PurchaseOrderId) {
        for (const request of buildPurchaseOrderRequests({ values, changedRows, ...options })) {
          const key = JSON.stringify([request.method, request.url, request.data]);
          if (completed.current.has(key)) continue;
          await send(request);
          if (!mounted.current) return;
          completed.current.add(key);
          await clearCacheForUrl(request.url);
        }
      }
      await clearCacheForUrl(getAllPurchaseOrders(500, 0).url);
      await clearCacheForUrl(getPurchaseOrderById(String(id)).url);
      const detail = await send(getPurchaseOrderById(String(id)));
      if (!mounted.current) return;
      if (String(detail?.PurchaseOrderId) !== String(id) || !Array.isArray(detail?.PurchaseOrderRows)) {
        updateMessageStatus({ requestUrl: getPurchaseOrderById(String(id)).url, status: 'error', message: 'The order was saved, but its details could not be reloaded. Retry to reload before making more changes.' });
        return;
      }
      setResult(detail);
      completed.current.clear();
      updateMessageStatus({ requestUrl: getPurchaseOrderById(String(id)).url, status: 'success', message: 'Purchase Order saved.' });
    } catch {
      // Transport reports failure. Keep edits and completed steps for a same-page retry.
    } finally {
      active.current = false;
      if (mounted.current) {
        setSubmitting(false);
        helpers.setSubmitting(false);
      }
    }
  };
  return [result, submit, submitting] as const;
};

export const buildPurchaseOrderRequests = ({ values, changedRows, nextStatusId, guards, baselineRows }: { values: any; changedRows: any[]; nextStatusId?: number; guards?: { formDirty: boolean; rowsChanged: boolean }; baselineRows?: any[]; }): AxiosRequestConfig[] => {
  const orderId = values?.PurchaseOrderId;
  const isCreate = orderId == null || orderId === 0;
  const ALWAYS_STATUS_ONLY = new Set<number>([STATUS_ID['Received Short'], STATUS_ID.Cancelled]);
  if (!isCreate && nextStatusId) {
    if (nextStatusId === STATUS_ID.Cancelled) return [cancelPurchaseOrder(orderId)];
    if (ALWAYS_STATUS_ONLY.has(nextStatusId)) {
      return [buildStatusOnlyRequest(orderId, nextStatusId)];
    }
  }

  // If we don't have an order id yet, fall back to "main create" only.
  if (isCreate) {
    const mainReq = buildPurchaseOrderRequest({ values, orderRows: changedRows, PurchaseOrderId: orderId });
    const mainRequests = Array.isArray(mainReq) ? mainReq : [mainReq];
    return mainRequests;
  }
  const nextRows = Array.isArray(changedRows) ? changedRows : [];
  const baseRows = Array.isArray(baselineRows) ? baselineRows : values?.PurchaseOrderRows || [];
  const baseById = new Map<number, any>();
  baseRows.forEach((r: any) => {
    const id = Number(r?.PurchaseOrderRowId);
    if (Number.isFinite(id) && id > 0) baseById.set(id, r);
  });
  const isSameDateish = (a: any, b: any) => {
    if (!a && !b) return true;
    const da = a ? new Date(a).toISOString() : '';
    const db = b ? new Date(b).toISOString() : '';
    return da === db;
  };

  // Decide if a row should be updated (keep this focused on fields you actually edit)
  const rowChanged = (prev: any, cur: any) => {
    if (!prev) return true;
    const n = (v: any) => (v == null || v === '' ? null : Number(v));
    const prevQty = n(prev?.Quantity);
    const curQty = n(cur?.Quantity);
    const prevPrice = n(prev?.NegotiatedPrice ?? prev?.ItemPriceOrderCurrency ?? prev?.Price ?? prev?.ListPriceDefaultCurrency);
    const curPrice = n(cur?.NegotiatedPrice ?? cur?.ItemPriceOrderCurrency ?? cur?.Price ?? cur?.ListPriceDefaultCurrency);
    const prevDiscAmt = n(prev?.DiscountAmount);
    const curDiscAmt = n(cur?.DiscountAmount);
    const prevComment = String(prev?.Comment ?? '');
    const curComment = String(cur?.Comment ?? '');
    if (prevQty !== curQty) return true;
    if (prevPrice !== curPrice) return true;
    if (prevDiscAmt !== curDiscAmt) return true;
    if (prevComment !== curComment) return true;
    if (!isSameDateish(prev?.ETA, cur?.ETA)) return true;
    if (!isSameDateish(prev?.RTA, cur?.RTA)) return true;
    return false;
  };

  const existingRows = nextRows.filter((r) => Number(r?.PurchaseOrderRowId ?? 0) > 0);
  const updateCandidates = existingRows.filter((r) => {
    const id = Number(r?.PurchaseOrderRowId);
    return rowChanged(baseById.get(id), r);
  });
  const policy = createPurchaseOrderPolicy({ statusId: values.Status ?? values.StatusId, rows: nextRows, env: { isExternalWarehouse: false } });
  const updateRows = updateCandidates.map((row) => {
    const payload = mapRowForUpsert(row);
    const fields = { Quantity: 'Row.Quantity', ItemPriceOrderCurrency: 'Row.ItemPrice', DiscountAmount: 'Row.DiscountPct', DiscountType: 'Row.DiscountPct', DisountType: 'Row.DiscountPct', ETA: 'Row.ETA', RTA: 'Row.RTA', Comment: 'Row.Comment' } as const;
    return Object.fromEntries(Object.entries(payload).filter(([key]) => key === 'PurchaseOrderRowId' || (fields[key] && policy.field(fields[key], row))));
  });
  const newRowsRaw = nextRows.filter((r) => Number(r?.PurchaseOrderRowId ?? 0) <= 0);
  const createRows = newRowsRaw.map(mapRowToCreatePayload).filter((r) => {
    const ok = !!r?.SupplierAssortmentItemId && typeof r.Quantity === 'number' && typeof r.ItemPriceOrderCurrency === 'number';
    return ok;
  });
  const originalIds = new Set<number>(baseRows.map((r: any) => Number(r?.PurchaseOrderRowId)).filter((id) => Number.isFinite(id) && id > 0));
  const keptIds = new Set<number>(existingRows.map((r: any) => Number(r?.PurchaseOrderRowId)).filter((id) => Number.isFinite(id) && id > 0));
  const removedIds = Array.from(originalIds).filter((id) => !keptIds.has(id));
  if (nextStatusId && !guards?.formDirty && !guards?.rowsChanged && !updateCandidates.length && !createRows.length && !removedIds.length)
    return [buildStatusOnlyRequest(orderId, nextStatusId)];
  const requests: AxiosRequestConfig[] = [];
  if (updateRows.length) requests.push(updatePurchaseOrderRowsById(orderId, updateRows));
  removedIds.forEach((id) => requests.push(deletePurchaseOrderRowById(orderId, id)));
  if (createRows.length) requests.push(createPurchaseOrderRowsById(orderId, createRows));

  // Always include main PO PATCH last (meta/status etc)
  const mainReq = buildPurchaseOrderRequest({ values, orderRows: nextRows, PurchaseOrderId: orderId });
  const mainRequests = Array.isArray(mainReq) ? mainReq : [mainReq];
  requests.push(...mainRequests);
  if (nextStatusId && orderId) requests.push(buildStatusOnlyRequest(orderId, nextStatusId));
  return requests;
};

const DEFAULT_DISCOUNT_TYPE = 3; // Amount

export const purchaseOrderRowsPostRequests = ({ values, changedRows }: { values: any; changedRows: any[] }): AxiosRequestConfig[] => {
  const orderId = values?.PurchaseOrderId;
  const originalRows: any[] = values?.PurchaseOrderRows || [];
  const existingChanged = (changedRows || []).filter((r) => (r?.PurchaseOrderRowId ?? 0) > 0);
  const newRowsRaw = (changedRows || []).filter((r) => (r?.PurchaseOrderRowId ?? 0) <= 0);
  const updateRows = existingChanged.map(mapRowForUpsert);
  const createRows = newRowsRaw.map(mapRowToCreatePayload);
  const originalIds = new Set(originalRows.map((r) => r.PurchaseOrderRowId).filter((id) => id > 0));
  const changedIds = new Set(existingChanged.map((r) => r.PurchaseOrderRowId).filter((id) => id > 0));
  const removedIds = [...originalIds].filter((id) => !changedIds.has(id));
  const requests: AxiosRequestConfig[] = [];
  if (updateRows.length) requests.push(updatePurchaseOrderRowsById(orderId, updateRows));
  if (createRows.length) requests.push(createPurchaseOrderRowsById(orderId, createRows));
  removedIds.forEach((id) => requests.push(deletePurchaseOrderRowById(orderId, id)));
  return requests;
};

const mapRowForUpsert = (row: any) => {
  const isExisting = (row?.PurchaseOrderRowId ?? 0) > 0;
  const normalizeNumber = (v: any) => (typeof v === 'number' && Number.isFinite(v) ? v : undefined);
  const tryParseDate = (d?: string | null) => {
    if (!d) return null;
    const dt = new Date(d);
    return isNaN(dt.getTime()) ? undefined : dt.toISOString();
  };
  const productId = row?.SupplierAssortmentItemId ?? row?.VendorAssortmentItemId;
  const qty = normalizeNumber(Number(row?.Quantity)) ?? 1;
  const price = normalizeNumber(row?.NegotiatedPrice) ?? normalizeNumber(row?.ItemPriceOrderCurrency) ?? normalizeNumber(row?.Price) ?? normalizeNumber(row?.ListPriceOrderCurrency) ?? normalizeNumber(row?.ListPriceDefaultCurrency);
  const eta = tryParseDate(row?.ETA);
  const rta = tryParseDate(row?.RTA);
  const discountAmount = normalizeNumber(row?.DiscountAmount);
  const disountType = row?.DisountType ?? row?.DiscountType; // keep server spelling if present
  const base: any = { ...(isExisting ? { PurchaseOrderRowId: row.PurchaseOrderRowId } : {}), SupplierAssortmentItemId: productId != null ? String(productId) : undefined, Quantity: qty, ItemPriceOrderCurrency: price, ETA: eta, RTA: rta, Comment: row?.Comment ?? '', DiscountAmount: discountAmount, DiscountType: DEFAULT_DISCOUNT_TYPE };
  if (typeof disountType !== 'undefined') base.DisountType = disountType;
  Object.keys(base).forEach((k) => base[k] === undefined && delete base[k]);
  return base;
};

export const buildPurchaseOrderRequest = (payload: { values: any; orderRows: any[]; PurchaseOrderId?: any }): AxiosRequestConfig => {
  return buildSubmitRequest({ values: payload, createOrUpdateFn: createOrUpdatePurchaseOrder, mapCreate: toPOPayload, mapUpdate: toPOPayloadUpdate, idField: 'PurchaseOrderId', updateMethod: 'PATCH' })!;
};

const intOrUndefined = (v: any) => {
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : undefined;
};

const tryParseDate = (d?: string | null) => {
  if (!d) return undefined;
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? undefined : dt.toISOString();
};

const numOrUndef = (n: any) => (typeof n === 'number' && Number.isFinite(n) ? n : undefined);

const mapRowToCreatePayload = (row: any) => {
  const productId = row?.SupplierAssortmentItemId ?? row?.VendorAssortmentItemId;
  const itemPrice = numOrUndef(row?.NegotiatedPrice) ?? numOrUndef(row?.ItemPriceOrderCurrency) ?? numOrUndef(row?.Price) ?? numOrUndef(row?.ListPriceOrderCurrency) ?? numOrUndef(row?.ListPriceDefaultCurrency);
  const qty = numOrUndef(row?.Quantity) ?? 1;
  const eta = tryParseDate(row?.ETA);
  const rta = tryParseDate(row?.RTA);
  const discountAmount = numOrUndef(row?.DiscountAmount);
  const disountType = row?.DisountType ?? row?.DiscountType;
  const out: any = { SupplierAssortmentItemId: productId != null ? String(productId) : undefined, ItemPriceOrderCurrency: itemPrice, Quantity: qty, ETA: eta, RTA: rta, Comment: row?.Comment ?? '', DiscountType: DEFAULT_DISCOUNT_TYPE, DiscountAmount: discountAmount ?? 0 };
  if (typeof disountType !== 'undefined') {
    out.DisountType = disountType; // keep API field spelling
  }
  Object.keys(out).forEach((k) => out[k] === undefined && delete out[k]);
  return out;
};

export const toPOPayload = ({ values, orderRows = [] }: { values: any; orderRows?: any[] }) => {
  const POData = { PurchaseOrderType: 1, Recipient: intOrUndefined(values.Recipient), SupplierId: intOrUndefined(values.Supplier), SupplierContact: values.SupplierContact || undefined, Status: intOrUndefined(values.Status), PaymentMethodId: intOrUndefined(values.PaymentMethodId), PaymentTermId: intOrUndefined(values.PaymentTermId), ShippingMethodId: intOrUndefined(values.ShippingMethod), ShippingTermId: intOrUndefined(values.ShippingTerm), ShippingTermsDestination: values.ShippingTermsDestination || undefined, SendDestination: values.SendDestination || undefined, SendType: intOrUndefined(values.SendType), Comment: values.Comment ?? '', PurchaseOrderRows: getMappedNewRows(orderRows) };
  return POData;
};

export const toPOPayloadUpdate = ({ values }: { values: any; orderRows?: any[] }) => {
  const policy = createPurchaseOrderPolicy({ statusId: values.Status ?? values.StatusId, env: { isExternalWarehouse: false } });
  const fields: Array<[any, string, any]> = [ ['Meta.Comment', 'Comment', values.Comment ?? ''], ['Meta.PaymentMethod', 'PaymentMethodId', intOrUndefined(values.PaymentMethodId ?? values.PaymentMethod)], ['Meta.PaymentTerm', 'PaymentTermId', intOrUndefined(values.PaymentTermId ?? values.PaymentTerm)], ['Meta.ShippingMethod', 'ShippingMethodId', intOrUndefined(values.ShippingMethod)], ['Meta.ShippingTerms', 'ShippingTermId', intOrUndefined(values.ShippingTerm)], ['Meta.ShippingTermsDestination', 'ShippingTermsDestination', values.ShippingTermsDestination ?? ''], ['Meta.SendDestination', 'SendDestination', values.SendDestination ?? ''], ['Meta.SendType', 'SendType', intOrUndefined(values.SendType)], ['Meta.CurrencyExchangeRate', 'CurrencyExchangeRate', values.CurrencyExchangeRate === '' ? undefined : numOrUndef(Number(values.CurrencyExchangeRate))] ];
  return Object.fromEntries(fields.filter(([field, , value]) => policy.field(field) && value !== undefined).map(([, key, value]) => [key, value]));
};

const getMappedNewRows = (orderRows: any[] = []) => (orderRows || []).filter((r) => (r?.PurchaseOrderRowId ?? -1) <= 0).map(mapRowToCreatePayload).filter((r) => !!r.SupplierAssortmentItemId && typeof r.Quantity === 'number' && typeof r.ItemPriceOrderCurrency === 'number');
/* -------------------- Minimal status-only PATCH request ------------------- */

const buildStatusOnlyRequest = (purchaseOrderId: number, statusId: number): AxiosRequestConfig => {
  return buildSubmitRequest({
    values: { values: { PurchaseOrderId: purchaseOrderId }, orderRows: [], PurchaseOrderId: purchaseOrderId },
    createOrUpdateFn: createOrUpdatePurchaseOrder,
    mapCreate: () => ({ Status: statusId }), // not used for create here
    mapUpdate: () => ({ Status: statusId }),
    idField: 'PurchaseOrderId',
    updateMethod: 'PATCH'
  })!;
};
